import React, { lazy, Suspense } from "react";
import { ThemeProvider, createTheme, CssBaseline } from "@mui/material";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import ErrorBoundary from "./utils/ErrorBoundary";
import ProtectedRoute from "./utils/ProtectedRoute";
import Navbar from "./components/Navbar/Navbar";
import { Login } from "./pages";
import "./App.css";
import { useAuth } from "./context/AuthContext";
import { GoogleMapsProvider } from "./context/GoogleMapsContext";

const Services = lazy(() => import("./components/dashboard/Services"));
const Leads = lazy(() => import("./components/dashboard/Leads"));
const LeadsDetails = lazy(() => import("./components/dashboard/LeadsDetails"));
const Languages = lazy(() => import("./components/dashboard/Languages"));
const Preferedzone = lazy(() => import("./components/dashboard/Preferedzone"));
const Status = lazy(() => import("./components/dashboard/Status"));
const Googlemapkey = lazy(() => import("./components/dashboard/Googlemapkey"));
const Availableshift = lazy(
  () => import("./components/dashboard/Availableshift"),
);
const DaysAvailable = lazy(
  () => import("./components/dashboard/DaysAvailable"),
);
const EducationLevel = lazy(
  () => import("./components/dashboard/EducationLevel"),
);
const States = lazy(() => import("./components/dashboard/States"));
const Districts = lazy(() => import("./components/dashboard/Districts"));
const Cities = lazy(() => import("./components/dashboard/Cities"));
const Coordinators = lazy(() => import("./components/dashboard/Coordinators"));
const Managers = lazy(() => import("./components/dashboard/Managers"));
const LeadSource = lazy(() => import("./components/dashboard/LeadSource"));
const InteractionType = lazy(
  () => import("./components/dashboard/InteractionType"),
);
const InteractionChannel = lazy(
  () => import("./components/dashboard/InteractionChannel"),
);
const InteractionOutcome = lazy(
  () => import("./components/dashboard/InteractionOutcome"),
);
const FollowUpMode = lazy(() => import("./components/dashboard/FollowUpMode"));
const ResourceType = lazy(() => import("./components/dashboard/ResourceType"));
const AssessmentType = lazy(
  () => import("./components/dashboard/AssessmentType"),
);
const AssessmentMode = lazy(
  () => import("./components/dashboard/AssessmentMode"),
);
const RiskCategory = lazy(() => import("./components/dashboard/RiskCategory"));
const ResourceCategory = lazy(
  () => import("./components/dashboard/ResourceCategory"),
);
const Relations = lazy(() => import("./components/dashboard/Relations"));
const Tariffs = lazy(() => import("./components/dashboard/Tariffs"));
const HospitalFacility = lazy(
  () => import("./components/dashboard/HospitalFacility"),
);
const FacilityService = lazy(
  () => import("./components/dashboard/FacilityService"),
);
const Hospitals = lazy(() => import("./components/dashboard/Hospitals"));
const HospitalDetails = lazy(
  () => import("./components/dashboard/HospitalDetails"),
);
const Carepreneur = lazy(() => import("./components/dashboard/Carepreneur"));
const CarepreneurDetails = lazy(
  () => import("./components/dashboard/CarepreneurDetails"),
);
const Blogposts = lazy(
  () => import("./components/dashboard/Marketing/Blogposts"),
);
const BlogCategory = lazy(
  () => import("./components/dashboard/Marketing/BlogCategory"),
);
const Contacts = lazy(
  () => import("./components/dashboard/Marketing/Contacts"),
);
const SeoUrl = lazy(() => import("./components/dashboard/SeoUrl"));

const PageLoader = () => (
  <div style={{ padding: 40, textAlign: "center" }}>Loading page...</div>
);

const theme = createTheme({
  palette: {
    primary: { main: "#7C3996" },
    background: { default: "#F3F4F6" },
  },
  typography: {
    fontFamily: '"Poppins", "Helvetica", "Arial", sans-serif',
  },
});

function App() {
  const { auth } = useAuth();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GoogleMapsProvider>
        <Router>
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route element={<ProtectedRoute />}>
                  <Route path="/" element={<Navbar />}>
                    <Route
                      index
                      element={
                        auth?.role === "Admin" ? (
                          <Navigate to="/worklist/myworklist" />
                        ) : null
                      }
                    />

                    <Route path="/settings/services" element={<Services />} />
                    <Route path="/settings/languages" element={<Languages />} />
                    <Route
                      path="/settings/prefered-zone"
                      element={<Preferedzone />}
                    />
                    <Route path="/settings/status" element={<Status />} />
                    <Route
                      path="/settings/googlemapkey"
                      element={<Googlemapkey />}
                    />
                    <Route
                      path="/settings/availble-shifts"
                      element={<Availableshift />}
                    />
                    <Route
                      path="/settings/days-available"
                      element={<DaysAvailable />}
                    />
                    <Route
                      path="/settings/education-levels"
                      element={<EducationLevel />}
                    />
                    <Route
                      path="/settings/hospital-facilities"
                      element={<HospitalFacility />}
                    />
                    <Route path="/settings/states" element={<States />} />
                    <Route path="/settings/districts" element={<Districts />} />
                    <Route path="/settings/cities" element={<Cities />} />
                    <Route
                      path="/settings/co-ordinator"
                      element={<Coordinators />}
                    />
                    <Route path="/settings/managers" element={<Managers />} />
                    <Route
                      path="/settings/lead-source"
                      element={<LeadSource />}
                    />
                    <Route
                      path="/settings/facility-service"
                      element={<FacilityService />}
                    />
                    <Route
                      path="/settings/interaction-type"
                      element={<InteractionType />}
                    />
                    <Route
                      path="/settings/interaction-channel"
                      element={<InteractionChannel />}
                    />
                    <Route
                      path="/settings/interaction-outcome"
                      element={<InteractionOutcome />}
                    />
                    <Route
                      path="/settings/followupmode"
                      element={<FollowUpMode />}
                    />
                    <Route
                      path="/settings/resourcetype"
                      element={<ResourceType />}
                    />
                    <Route
                      path="/settings/assessment-type"
                      element={<AssessmentType />}
                    />
                    <Route
                      path="/settings/assessment-mode"
                      element={<AssessmentMode />}
                    />
                    <Route
                      path="/settings/risk-category"
                      element={<RiskCategory />}
                    />
                    <Route
                      path="/settings/resource-category"
                      element={<ResourceCategory />}
                    />
                    <Route path="/settings/relations" element={<Relations />} />
                    <Route path="/settings/tariffs" element={<Tariffs />} />

                    <Route path="/worklist/myworklist" element={<Leads />} />

                    <Route
                      path="/worklist/myworklist/:id"
                      element={<LeadsDetails />}
                    />

                    <Route
                      path="/worklist/carepreneur"
                      element={<Carepreneur />}
                    />
                    <Route
                      path="/worklist/carepreneur/add"
                      element={<CarepreneurDetails />}
                    />
                    <Route
                      path="/worklist/carepreneur/:id"
                      element={<CarepreneurDetails />}
                    />

                    <Route path="/worklist/hospital" element={<Hospitals />} />
                    <Route
                      path="/worklist/hospital/add"
                      element={<HospitalDetails />}
                    />
                    <Route
                      path="/worklist/hospital/:id"
                      element={<HospitalDetails />}
                    />

                    <Route
                      path="/marketing/blog-categories"
                      element={<BlogCategory />}
                    />
                    <Route
                      path="/marketing/blog-posts"
                      element={<Blogposts />}
                    />
                    <Route path="/marketing/contacts" element={<Contacts />} />
                    <Route path="/marketing/seo-url" element={<SeoUrl />} />
                  </Route>
                </Route>
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </Router>
      </GoogleMapsProvider>
    </ThemeProvider>
  );
}

export default App;
